/**
 * OSR lead filters, remembered per person on this device.
 *
 * Same shape and habits as Sales Intake's (components/calls/useIntakeFilters.js):
 * a Set means "only these", null means "everything". Stored under its own
 * key, so the page keeps its filters when it moves.
 *
 * Storage is a convenience, never a requirement: with cookies blocked, iOS
 * Safari throws on any localStorage access, so every read and write is
 * guarded and the page simply starts from the defaults.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { computeDateRange, fmtRangeDay, DATE_PRESETS } from "../calls/liveCallsShared";
import { OUTCOME_BY_VALUE, SORT_OPTIONS, sourceLabel } from "./osrShared";

const KEY_PREFIX = "chariot.osr.filters.v1";
const keyFor = (userId) => `${KEY_PREFIX}.${userId || "anon"}`;
const VIEWS = ["working", "followups", "closed", "spam", "scorecard"];
const SORTS = SORT_OPTIONS.map((o) => o.value);

export const OSR_FILTER_DEFAULTS = {
  view: "working",
  search: "",
  dateFilter: { preset: "all", from: "", to: "" },
  users: null,
  markets: null,
  sources: null,
  outcomes: null,
  sort: "",
};

const toSet = (v) => (Array.isArray(v) ? new Set(v.map(String)) : null);

export function loadOsrFilters(userId) {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const p = JSON.parse(raw);
    const out = {};
    if (VIEWS.includes(p.view)) out.view = p.view;
    if (typeof p.search === "string") out.search = p.search.slice(0, 120);
    if (p.dateFilter && typeof p.dateFilter.preset === "string") {
      out.dateFilter = { preset: p.dateFilter.preset, from: String(p.dateFilter.from || ""), to: String(p.dateFilter.to || "") };
    }
    for (const key of ["users", "markets", "sources", "outcomes"]) if (Array.isArray(p[key])) out[key] = toSet(p[key]);
    if (SORTS.includes(p.sort)) out.sort = p.sort;
    return out;
  } catch {
    return {};
  }
}

export function saveOsrFilters(userId, state) {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify({
      view: state.view,
      search: state.search,
      dateFilter: state.dateFilter,
      users: state.users ? [...state.users] : null,
      markets: state.markets ? [...state.markets] : null,
      sources: state.sources ? [...state.sources] : null,
      outcomes: state.outcomes ? [...state.outcomes] : null,
      sort: state.sort,
    }));
  } catch {
    /* storage unavailable — filters just won't persist */
  }
}

const presetLabel = (preset) => (DATE_PRESETS.find((p) => p.value === preset) || {}).label || "Custom";

/** The query string the list and the scorecard send. */
export function filterQuery(filters) {
  const q = new URLSearchParams();
  const { from, to } = computeDateRange(filters.dateFilter);
  if (from) q.set("date_from", from.toISOString());
  if (to) q.set("date_to", to.toISOString());
  if (filters.search.trim()) q.set("search", filters.search.trim());
  for (const key of ["users", "markets", "sources", "outcomes"]) {
    if (filters[key] && filters[key].size) q.set(key, [...filters[key]].join(","));
  }
  if (filters.sort) q.set("sort", filters.sort);
  return q;
}

/**
 * @param {string} userId
 * @param {object} meta  { users, markets } from GET /admin/osr/meta, for chip labels
 */
export default function useOsrFilters(userId, meta) {
  const [state, setState] = useState(() => ({ ...OSR_FILTER_DEFAULTS, ...loadOsrFilters(userId) }));
  useEffect(() => { saveOsrFilters(userId, state); }, [userId, state]);

  const set = useCallback((patch) => setState((s) => ({ ...s, ...(typeof patch === "function" ? patch(s) : patch) })), []);
  const clearAll = useCallback(() => setState((s) => ({ ...OSR_FILTER_DEFAULTS, view: s.view })), []);

  // An empty Set means "nothing ticked", which would hide every lead; the
  // server reads a missing filter as "everything", so treat it as cleared.
  const active = useMemo(() => {
    const on = (v) => v instanceof Set && v.size > 0;
    return {
      date: state.dateFilter.preset !== "all",
      users: on(state.users),
      markets: on(state.markets),
      sources: on(state.sources),
      outcomes: on(state.outcomes),
      search: !!state.search.trim(),
    };
  }, [state]);
  const activeCount = ["date", "users", "markets", "sources", "outcomes"].filter((k) => active[k]).length;

  const chips = useMemo(() => {
    const out = [];
    const nameOf = (list, id, fallback) => (list || []).find((x) => String(x.id) === String(id))?.name || fallback;
    const some = (set, label) => {
      const all = [...set].map(label);
      return all.length <= 2 ? all.join(", ") : `${all.slice(0, 2).join(", ")} +${all.length - 2}`;
    };
    if (active.search) out.push({ key: "search", label: `Search: “${state.search.trim()}”`, onClear: () => set({ search: "" }) });
    if (active.date) {
      const { from, to } = computeDateRange(state.dateFilter);
      const label = state.dateFilter.preset === "custom" ? `${fmtRangeDay(from)} – ${fmtRangeDay(to)}` : presetLabel(state.dateFilter.preset);
      out.push({ key: "date", label: `Arrived: ${label}`, onClear: () => set({ dateFilter: OSR_FILTER_DEFAULTS.dateFilter }) });
    }
    if (active.users) out.push({ key: "users", label: `People: ${some(state.users, (id) => nameOf(meta?.users, id, "Someone"))}`, onClear: () => set({ users: null }) });
    if (active.markets) out.push({ key: "markets", label: `Market: ${some(state.markets, (id) => (id === "unassigned" ? "No market" : nameOf(meta?.markets, id, "A market")))}`, onClear: () => set({ markets: null }) });
    if (active.sources) out.push({ key: "sources", label: `Source: ${some(state.sources, sourceLabel)}`, onClear: () => set({ sources: null }) });
    if (active.outcomes) out.push({ key: "outcomes", label: `Status: ${some(state.outcomes, (o) => OUTCOME_BY_VALUE[o]?.label || o)}`, onClear: () => set({ outcomes: null }) });
    if (state.sort) out.push({ key: "sort", label: `Sort: ${(SORT_OPTIONS.find((o) => o.value === state.sort) || {}).label || state.sort}`, onClear: () => set({ sort: "" }) });
    return out;
  }, [active, state, meta, set]);

  return { filters: state, set, clearAll, activeCount, chips };
}
